import { useRef } from "react";
import SoftwarePageTemplate from "@/components/templates/SoftwarePageTemplate";
import GlobalNavigationBar from "@/v2-components/global-navigation-bar";
import PromoBanner from "@/components/banner/PromoBanner";
import ReferralBanner from "@/components/banner/ReferralBanner";
import SuggestLocale from "@/components/SuggestLocale";
import { useLocale } from "@/store/locale-store";
import { getSoftwareServerSideProps } from "@/libs/getSoftwareServerSideProps";
import { GetServerSidePropsContext } from "next";
import { VrpFaqSection } from "@/v2-components/pages/software/components/vrplayer/faq-section";
import GamesSubNav from "@/v2-components/pages/games/GamesSubNav";
import SwHeadBand from "@/v2-components/pages/software/vr-software/sw-head-band";
import SwPlayerSection from "@/v2-components/pages/software/vr-software/sw-player-section";
import { PLAYER_SECTIONS } from "@/v2-components/pages/software/vr-software/sw-config";
import SwFeedbackBand from "@/v2-components/pages/software/vr-software/sw-feedback-band";

const VRP_COVER_DESKTOP =
  "/images/software/newVrplayer/vrp-banner-desktop.webp";

const VrpPlayerPage = ({ countryCode }: { countryCode: string }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;

  return (
    <SoftwarePageTemplate imageSrc={VRP_COVER_DESKTOP}>
      <SoftwarePageTemplate.Content
        useCustomNav={true}
        className="bg-[#F2F4F7]"
      >
        <div
          ref={headerRef}
          data-sw-sticky-header
          className="sticky top-0 z-30"
        >
          {canSuggeset && <SuggestLocale countryCode={countryCode} />}
          <ReferralBanner />
          <GlobalNavigationBar />
          <PromoBanner />
          {/* ★탭 바를 여기 둔다 — 이 화면은 **PC/VR Games 의 세 번째 탭**이다
              (GNB 시안 `29212:35874`·`29212:35926`: Overview · 지원 게임 전체 ·
              Software). 종전엔 `/software/{pc,vr}player` 라 탭을 누르면 탭 바가 통째로
              사라졌다 — 실측으로 서브내비 링크가 12개에서 2개로 줄었다.
              `GamesSubNav` 는 프롭이 아니라 `router.pathname` 으로 PC/VR 을 가르므로
              라우트만 맞으면 알아서 붙는다. */}
          <GamesSubNav />
        </div>
        {/**
         * 개편 시안 `29889:102282`(데스크탑) · `30035:23192`(모바일) — #5475.
         *
         * 한 페이지에 플레이어 셋(PC · VR · Mobile)을 담고, 히어로의 기기 카드가 각
         * 섹션 앵커로 내려간다. 「VR Games」 하위인데 PC Player 가 있는 이유는 PC VR
         * (SteamVR 헤드셋)도 VR 범주이기 때문이다.
         *
         * 섹션 간격은 시안이 **96(데스크탑) / 48(모바일)** 이라 컨테이너 `gap` 으로 준다 —
         * 섹션마다 `pb` 를 걸면 마지막 섹션에도 붙고 조건부 섹션이 빠질 때 같이 사라진다.
         *
         * ⚠️ 종전 섹션 6개(`VrpCover`·`DownloadBar`·`SupportedHeadsets`·`Feature`·
         * `Download`·`Feedback`)는 이 시안이 **대체**한다. `VrpFaqSection` 은 시안에도
         * FAQ 가 있어(`29889:102433`) 그대로 쓴다.
         */}
        <div className="flex w-full flex-col gap-12 bg-white py-12 laptop:gap-24 laptop:py-24">
          <SwHeadBand />
          {PLAYER_SECTIONS.map((section) => (
            <SwPlayerSection key={section.id} section={section} />
          ))}
          {/**
           * ★FAQ 는 시안이 **952 중앙**이다(`29889:102433` — x=484 w=952,
           * (1920−952)/2 = 484). `VrpFaqSection` 은 `max-w-[1440px]` 이라 그대로 두면
           * 두 배로 벌어진다. 공유 컴포넌트라 여기서 감싼다(다른 소비처 영향 없음).
           */}
          <div className="flex w-full justify-center">
            {/* ⚠️ `VrpFaqSection` 안쪽에 `px-4 laptop:px-5`(좌우 20)가 있어서 952 로
                감싸면 내용이 912 가 된다(실측). 40 을 더해 **내용이 952** 가 되게 한다. */}
            {/* ⚠️ `VrpFaqSection` 은 종전 페이지용이라 `py-12 laptop:py-16`(64)을 갖고 있다.
                시안 FAQ(`29889:102433` h=328 = 제목 40 + 갭 32 + 목록 256)에는 그 패딩이
                없어서, 컨테이너 `gap-24`(96) 위에 64 가 더해져 **보이는 갭이 180** 이었다
                (실측). 여기서 죽인다. */}
            <div className="w-full max-w-[992px] [&_section]:py-0">
              <VrpFaqSection />
            </div>
          </div>
          {/**
           * 피드백 배너 — 시안 `30035:23462` · `30035:23463`.
           * 초판은 `get_metadata` 의 「자식 0개」를 빈 프레임으로 읽어 자리표시를 넣었고,
           * 그다음엔 기존 `VrpFeedbackSection`(좌측 제목 + 우측 이미지)을 붙였는데
           * 시안은 **가운데 정렬 밴드**라 레이아웃이 다르다. 시안대로 새로 만들었다.
           */}
          <SwFeedbackBand />
        </div>
      </SoftwarePageTemplate.Content>
    </SoftwarePageTemplate>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  return getSoftwareServerSideProps(ctx);
}

export default VrpPlayerPage;
